import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { RiArrowRightUpLine } from 'react-icons/ri';
import { useAppLanguage } from '../../../Hooks/useAppLanguage';
import { useOnlinePaymentStatus } from '../../../Hooks/useOnlinePaymentStatus';
import { ordersAPI, paymentsAPI } from '../../../Api/api';
import { formatOrderItemVariantLabel, formatOrderMoney } from '../../../Utils/orderDisplay';
import { rememberPendingOnlinePayment, takePendingOnlinePayment } from '../../../Utils/pendingOnlinePayment';
import { usePublicSettings } from '../../../Context/PublicSettingsContext';
import { useCart } from '../../../Context/CartContext';
import { useToast } from '../../../Context/ToastContext';
import CheckoutFlowHeader from '../CheckoutFlowHeader';
import KineticLoader from '../../../Component/UI/KineticLoader/KineticLoader';
import ProductCardImage from '../../../Component/UI/ProductCard/ProductCardImage';
import './OrderConfirmation.css';

const resolveItemName = (name, lang) => {
    if (name && typeof name === 'object') {
        return name[lang] || name.en || Object.values(name)[0] || '';
    }
    return String(name || '');
};

// Which payment message an unpaid online order shows, by polling phase.
const PAYMENT_VIEW_BY_PHASE = {
    idle: 'confirming',
    checking: 'confirming',
    settled: 'confirming',
    awaiting: 'notCompleted',
    slow: 'slow',
    closed: 'failed',
};

const OrderConfirmation = () => {
    const { t, currentLanguage, langPath, dir } = useAppLanguage();
    // Subscribed so a store currency change re-renders the totals below.
    usePublicSettings();
    const { clearCart } = useCart();
    const { addToast } = useToast();
    const showTrackOrdersLinks = (process.env.REACT_APP_SHOW_TRACK_ORDERS_LINKS || 'true').toLowerCase() === 'true';
    const [searchParams] = useSearchParams();
    const orderNumber = searchParams.get('orderNumber');
    // Set by the payment provider when the customer left its page without paying.
    const leftPaymentPage = searchParams.get('payment') === 'cancelled';
    const [orderDetails, setOrderDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [retrying, setRetrying] = useState(false);

    const fetchOrderDetails = useCallback(async () => {
        setLoadError(false);
        try {
            const response = await ordersAPI.getOrderByNumber(orderNumber);
            if (response?.data?.status === 'success') {
                setOrderDetails(response.data.data);
            } else {
                setLoadError(true);
            }
        } catch (error) {
            console.error('Error fetching order details:', error);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, [orderNumber]);

    useEffect(() => {
        if (orderNumber) {
            fetchOrderDetails();
        } else {
            setLoading(false);
        }
    }, [orderNumber, fetchOrderDetails]);

    const isOnlineOrder = orderDetails?.payment?.paymentMethod === 'online';
    const awaitingPayment = isOnlineOrder && orderDetails?.orderStatus === 'awaiting-payment';
    const { phase, checkAgain } = useOnlinePaymentStatus(orderNumber, {
        enabled: awaitingPayment,
        leftPaymentPage,
    });

    // The payment was confirmed or the order closed: show the order as it now stands.
    useEffect(() => {
        if (phase === 'settled' || phase === 'closed') {
            fetchOrderDetails();
        }
    }, [phase, fetchOrderDetails]);

    // The cart was kept in case the payment failed; once paid it can go.
    const isPaid = orderDetails?.payment?.paymentStatus === 'paid';
    useEffect(() => {
        if (isOnlineOrder && isPaid && takePendingOnlinePayment(orderNumber)) {
            clearCart();
        }
    }, [isOnlineOrder, isPaid, orderNumber, clearCart]);

    let paymentView = null;
    if (isOnlineOrder && orderDetails?.payment?.paymentStatus !== 'paid') {
        if (orderDetails.orderStatus === 'cancelled') {
            paymentView = 'failed';
        } else if (awaitingPayment) {
            paymentView = PAYMENT_VIEW_BY_PHASE[phase] || 'confirming';
        }
    }

    const handleRetryPayment = async () => {
        setRetrying(true);
        try {
            const response = await paymentsAPI.retry(orderNumber, langPath('/order-confirmation'));
            const redirectUrl = response?.data?.checkout?.redirectUrl;
            if (!redirectUrl) throw new Error('Missing payment page');
            rememberPendingOnlinePayment(orderNumber);
            window.location.assign(redirectUrl);
        } catch (error) {
            addToast(error?.response?.data?.message || t('orderConfirmation.payment.retryFailed'), 'error');
            setRetrying(false);
        }
    };

    const getStatusText = (status) => {
        const key = status?.toLowerCase?.();
        const map = {
            'awaiting-payment': t('orderConfirmation.statusAwaitingPayment'),
            pending: t('orderConfirmation.statusPending'),
            confirmed: t('orderConfirmation.statusConfirmed'),
            processing: t('orderConfirmation.statusProcessing'),
            shipped: t('orderConfirmation.statusShipped'),
            delivered: t('orderConfirmation.statusDelivered'),
            completed: t('orderConfirmation.statusCompleted'),
            cancelled: t('orderConfirmation.statusCancelled'),
            refunded: t('orderConfirmation.statusRefunded'),
        };
        return map[key] || status;
    };

    const getPaymentMethodLabel = (method) => {
        const key = method?.toLowerCase?.();
        const map = {
            cash: t('orderConfirmation.paymentCash'),
            card: t('orderConfirmation.paymentCard'),
            'bank-transfer': t('orderConfirmation.paymentBankTransfer'),
            online: t('orderConfirmation.paymentOnline'),
            wallet: t('orderConfirmation.paymentWallet'),
        };
        return map[key] || method;
    };

    const shippingAddress = orderDetails?.customerInfo?.shippingAddress;
    const hasShippingAddress = Boolean(
        shippingAddress?.street || shippingAddress?.city || shippingAddress?.postalCode
    );

    return (
        <main className="main checkout-flow-main">
            <CheckoutFlowHeader backTo="/products" backLabel={t('orderConfirmation.continueShopping')} />
            <section className="order-success-page">
                <div className="container order-success-header" dir={dir}>
                    <span className="order-success-eyebrow">
                        {paymentView ? t('orderConfirmation.payment.eyebrow') : t('orderConfirmation.eyebrow')}
                    </span>
                    <h1 className="order-success-page-title">
                        {paymentView ? t(`orderConfirmation.payment.${paymentView}Title`) : t('orderConfirmation.title')}
                    </h1>
                    <p className="order-success-lead">
                        {paymentView ? t(`orderConfirmation.payment.${paymentView}Text`) : t('orderConfirmation.description')}
                    </p>
                </div>

                <div className="container" dir={dir}>
                    <div className="order-success-card">
                        {paymentView === 'confirming' ? (
                            <div className="order-success-loading" role="status">
                                <KineticLoader />
                            </div>
                        ) : !paymentView ? (
                            <div className="order-success-visual" aria-hidden="true">
                                <div className="order-success-ring">
                                    <svg viewBox="0 0 60 60" fill="none">
                                        <circle
                                            cx="30"
                                            cy="30"
                                            r="28"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeDasharray="176"
                                            strokeDashoffset="0"
                                            className="order-success-circle"
                                        />
                                        <path
                                            d="M18 30l9 9 15-15"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="order-success-check"
                                        />
                                    </svg>
                                </div>
                            </div>
                        ) : null}

                        {paymentView && paymentView !== 'confirming' ? (
                            <div className="order-success-actions">
                                {paymentView === 'notCompleted' && (
                                    <button
                                        type="button"
                                        className="order-success-btn order-success-btn--primary"
                                        onClick={handleRetryPayment}
                                        disabled={retrying}
                                    >
                                        <span>{t('orderConfirmation.payment.tryAgain')}</span>
                                    </button>
                                )}
                                {paymentView === 'slow' && (
                                    <button
                                        type="button"
                                        className="order-success-btn order-success-btn--primary"
                                        onClick={checkAgain}
                                    >
                                        <span>{t('orderConfirmation.payment.checkAgain')}</span>
                                    </button>
                                )}
                                {(paymentView === 'notCompleted' || paymentView === 'failed') && (
                                    <Link to={langPath('/cart')} className="order-success-btn order-success-btn--outline">
                                        {t('orderConfirmation.payment.backToCart')}
                                    </Link>
                                )}
                            </div>
                        ) : null}

                        {loading ? (
                            <div className="order-success-loading" role="status">
                                <KineticLoader />
                                <p>{t('orderConfirmation.loading')}</p>
                            </div>
                        ) : orderDetails ? (
                            <div className="order-success-details">
                                <div className="order-success-dl">
                                    <div className="order-success-row">
                                        <span className="order-success-label">{t('orderConfirmation.orderNumber')}</span>
                                        <span className="order-success-value order-success-value--accent">
                                            {orderDetails.orderNumber}
                                        </span>
                                    </div>

                                    <div className="order-success-row">
                                        <span className="order-success-label">{t('orderConfirmation.paymentMethod')}</span>
                                        <span className="order-success-value">
                                            {getPaymentMethodLabel(orderDetails.payment?.paymentMethod)}
                                        </span>
                                    </div>

                                    <div className="order-success-row">
                                        <span className="order-success-label">{t('orderConfirmation.total')}</span>
                                        <span className="order-success-value order-success-value--total">
                                            {formatOrderMoney(orderDetails.totalAmount, orderDetails.currency)}
                                        </span>
                                    </div>

                                    <div className="order-success-row">
                                        <span className="order-success-label">{t('orderConfirmation.status')}</span>
                                        <span className="order-success-pill">{getStatusText(orderDetails.orderStatus)}</span>
                                    </div>

                                    {orderDetails.shipping?.estimatedDeliveryDate && (
                                        <div className="order-success-row">
                                            <span className="order-success-label">{t('orderConfirmation.estimatedDelivery')}</span>
                                            <span className="order-success-value">
                                                {new Date(orderDetails.shipping.estimatedDeliveryDate).toLocaleDateString(
                                                    currentLanguage
                                                )}
                                            </span>
                                        </div>
                                    )}

                                    {hasShippingAddress && (
                                        <div className="order-success-row order-success-row--stack">
                                            <span className="order-success-label">{t('orderConfirmation.shippingAddress')}</span>
                                            <span className="order-success-value order-success-value--multiline">
                                                {shippingAddress.street}
                                                {shippingAddress.apartment ? `, ${shippingAddress.apartment}` : ''}
                                                <br />
                                                {[shippingAddress.city, shippingAddress.state, shippingAddress.postalCode]
                                                    .filter(Boolean)
                                                    .join(', ')}
                                                {shippingAddress.country ? (
                                                    <>
                                                        <br />
                                                        {shippingAddress.country}
                                                    </>
                                                ) : null}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div className="order-success-items">
                                    <h2 className="order-success-items-title">{t('orderConfirmation.orderItems')}</h2>
                                    <ul className="order-success-item-list">
                                        {orderDetails.items?.map((item, index) => {
                                            const variantLabel = formatOrderItemVariantLabel(item);
                                            const itemName = resolveItemName(item.name, currentLanguage);
                                            const imageProduct = {
                                                _id: item.productId,
                                                imageUrl: item.primaryImage,
                                                primaryImage: item.primaryImage,
                                            };
                                            return (
                                                <li key={index} className="order-success-item">
                                                    <div className="order-success-item-main">
                                                        <div className="order-success-item-thumb">
                                                            <ProductCardImage
                                                                product={imageProduct}
                                                                productName={itemName}
                                                                alt={itemName}
                                                                className="order-success-item-img"
                                                                compact
                                                            />
                                                        </div>
                                                        <div className="order-success-item-text">
                                                            <span className="order-success-item-name">
                                                                {itemName}
                                                                {variantLabel ? (
                                                                    <span className="order-success-item-name-size">
                                                                        {' '}
                                                                        ({variantLabel})
                                                                    </span>
                                                                ) : null}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <span className="order-success-item-qty">×{item.quantity}</span>
                                                    <span className="order-success-item-price">
                                                        {formatOrderMoney(item.price, orderDetails.currency)}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </div>
                        ) : orderNumber ? (
                            <div className="order-success-fallback">
                                {loadError ? (
                                    <p className="order-success-fallback-note">{t('orderConfirmation.loadError')}</p>
                                ) : null}
                                <p className="order-success-fallback-label">{t('orderConfirmation.orderNumberLabel')}</p>
                                <p className="order-success-fallback-number">{orderNumber}</p>
                                <p className="order-success-fallback-note">{t('orderConfirmation.trackNote')}</p>
                            </div>
                        ) : (
                            <div className="order-success-fallback">
                                <p className="order-success-fallback-note">{t('orderConfirmation.noOrderDetails')}</p>
                            </div>
                        )}

                        <div className="order-success-actions">
                            <Link to={langPath('/products')} className="order-success-btn order-success-btn--primary">
                                <span>{t('orderConfirmation.continueShopping')}</span>
                                <RiArrowRightUpLine className="order-success-btn-ico" aria-hidden />
                                <span className="order-success-btn-shine" aria-hidden />
                            </Link>
                            {showTrackOrdersLinks && orderNumber && (
                                <Link
                                    to={langPath(`/track-order?orderNumber=${encodeURIComponent(orderNumber)}`)}
                                    className="order-success-btn order-success-btn--outline"
                                >
                                    {t('orderConfirmation.trackOrder')}
                                </Link>
                            )}
                            <Link to={langPath('/')} className="order-success-btn order-success-btn--outline">
                                {t('orderConfirmation.goHome')}
                            </Link>
                        </div>

                        <div className="order-success-foot">
                            <p>{t('orderConfirmation.emailConfirmation')}</p>
                            <p>{t('orderConfirmation.contactInfo')}</p>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default OrderConfirmation;
